import {
    Platform
} from "react-native";
import type {
    ResizableStoredLayoutType
} from "./type";
import {
    RESIZABLE_STORAGE_PREFIX
} from "./util";

type AsyncStorageType = {
    setItem: (key: string, value: string) => Promise<void>;
    getItem: (key: string) => Promise<string | null>;
};

const getStorageKey = (persistKey: string): string => {
    return `${RESIZABLE_STORAGE_PREFIX}${persistKey}`;
};

const isStoredLayout = (value: unknown): value is ResizableStoredLayoutType => {
    if(typeof value !== "object" || value === null) {
        return false;
    }

    return "percents" in value && Array.isArray(value.percents) && "collapsed" in value && Array.isArray(value.collapsed);
};

const parseStoredLayout = (raw: string | null): ResizableStoredLayoutType | undefined => {
    if(!raw) {
        return undefined;
    }

    try {
        const parsed: unknown = JSON.parse(raw);

        return isStoredLayout(parsed) ? parsed : undefined;
    } catch {
        return undefined;
    }
};

const getAsyncStorage = (): AsyncStorageType | undefined => {
    try {
        // eslint-disable-next-line @typescript-eslint/no-require-imports
        const module = require("@react-native-async-storage/async-storage");

        return module?.default ?? module;
    } catch {
        return undefined;
    }
};

export const readWebLayout = (persistKey: string): ResizableStoredLayoutType | undefined => {
    if(Platform.OS !== "web") {
        return undefined;
    }

    try {
        return parseStoredLayout(globalThis.localStorage.getItem(getStorageKey(persistKey)));
    } catch {
        return undefined;
    }
};

export const readNativeLayout = async (persistKey: string): Promise<ResizableStoredLayoutType | undefined> => {
    try {
        const raw = await getAsyncStorage()?.getItem(getStorageKey(persistKey));

        return parseStoredLayout(raw ?? null);
    } catch {
        return undefined;
    }
};

export const writeLayout = (persistKey: string, layout: ResizableStoredLayoutType) => {
    const raw = JSON.stringify(layout);

    try {
        if(Platform.OS === "web") {
            globalThis.localStorage.setItem(getStorageKey(persistKey), raw);

            return;
        }

        getAsyncStorage()?.setItem(getStorageKey(persistKey), raw);
    } catch {
        return;
    }
};
